import { useState } from 'react';
import { fill, Size, t } from '@bms2/shared';
import { Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { TERMS_SETS_PATH, termsClientPath, type TermsSetsAnswer } from '../settings/termsSetsApi';
import { put, useApi } from './api';

/**
 * 🔒 The client's terms set, chosen from the Company page's Access & agreements
 * tile (T333; decision 1172): a set every document to this client prints, or
 * the class default. A set a confidentiality role adds is never offered, since
 * it prints wherever its role reaches whatever is chosen here (decision 1171).
 *
 * 🔨 It is mounted only while open, so a second opening reads the sets again
 * rather than the choice the first one left.
 */
export interface CompanyTermsSetModalProps {
  companyId: string;
  companyName: string;
  /** The set the client prints today, or null for the class default. */
  setId: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

const CLASS_DEFAULT = '';

export function CompanyTermsSetModal({ companyId, companyName, setId, apiBaseUrl = '', onCancel, onSaved }: CompanyTermsSetModalProps) {
  const sets = useApi<TermsSetsAnswer>(TERMS_SETS_PATH, apiBaseUrl);
  const [choice, setChoice] = useState(setId ?? CLASS_DEFAULT);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const offered = sets.state.kind === 'ready' ? sets.state.data.sets.filter((set) => !set.confidentialityRoleId) : [];

  const save = async () => {
    setSaving(true);
    const outcome = await put(termsClientPath(companyId), { setId: choice === CLASS_DEFAULT ? null : choice }, apiBaseUrl);
    setSaving(false);
    if (!outcome.ok) return setError(outcome.message);
    onSaved();
  };

  return (
    <Modal
      open
      title={fill(t('company.termsSet.title'), { name: companyName })}
      onCancel={onCancel}
      dataModal="company-terms-set"
      actions={[
        { kind: 'primary', label: t('modal.save'), onAct: () => void save(), disabled: saving || sets.state.kind !== 'ready' },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={modalStyles.fieldLabel}>{t('company.termsSet.hint')}</p>
      <Select
        size={Size.Large}
        label={t('company.termsSet.field')}
        value={choice}
        onChange={setChoice}
        options={[{ value: CLASS_DEFAULT, label: t('company.termsSet.useDefault') }, ...offered.map((set) => ({ value: set.id, label: set.name }))]}
        dataAttribute={{ 'data-company-terms-set': companyId }}
      />
    </Modal>
  );
}
